import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminText,
  UpdateTextNewImageBody,
  UpdateTextRemoveImageBody,
} from "../../../../../http/vertigrip/types/texts";
import { useTextsApi } from "../../../../../http/vertigrip/useTexts";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Textarea from "../../../../ui/Textarea";
import TranslatableField from "../../../../ui/TranslatableField";

interface UpdateTextForm {
  value: string | null;
  valueIt: string | null;
  valueDe: string | null;
  valueEs: string | null;
  valueFr: string | null;
}

interface TextDetailsProps {
  text?: AdminText;
  disabled?: boolean;
}

const TextDetails: FC<TextDetailsProps> = ({ text, disabled }: TextDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textsApi = useTextsApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: text?.image,
    isLoading: !text,
    disabled,
  });

  const defaultValues: UpdateTextForm = {
    value: text?.value ?? null,
    valueIt: text?.valueIt ?? null,
    valueDe: text?.valueDe ?? null,
    valueEs: text?.valueEs ?? null,
    valueFr: text?.valueFr ?? null,
  };

  const submit = async (body: UpdateTextNewImageBody | UpdateTextRemoveImageBody) => {
    try {
      await textsApi.updateAdminText(text?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateTextForm) => {
    setIsSubmitting(true);

    const commonBody: UpdateTextNewImageBody | UpdateTextRemoveImageBody = {
      value: formData.value?.trim()!,
      valueIt: formData.valueIt?.trim() ?? null,
      valueDe: formData.valueDe?.trim() ?? null,
      valueEs: formData.valueEs?.trim() ?? null,
      valueFr: formData.valueFr?.trim() ?? null,
    };

    if (newImage) {
      try {
        const signedUrl = await textsApi.generateUploadImageUrl(text?.id!, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateTextNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateTextRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateTextForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [text]);

        useEffect(() => {
          register("valueIt");
          register("valueDe");
          register("valueEs");
          register("valueFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label="Id">
              <Input disabled={true} defaultValue={text?.id} />
            </FormField>
            <FormField label={i18n.translation.common.value} error={errors.value}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="value"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.value}
                    dialogType="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("value", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default TextDetails;
